import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../CSS/LoginComponent.css';

const LoginComponent: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    rememberMe: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Backend API URL - adjust this to match your backend server
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:3060';

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: formData.username,
          password: formData.password
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      if (data.success && data.data?.token) {
        // Store token based on rememberMe preference
        if (formData.rememberMe) {
          localStorage.setItem('adminToken', data.data.token);
          localStorage.setItem('adminUser', JSON.stringify(data.data.user));
        } else {
          sessionStorage.setItem('adminToken', data.data.token);
          sessionStorage.setItem('adminUser', JSON.stringify(data.data.user));
        }

        // Redirect to Job Directory page
        navigate('/admin/job-directory');
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred during login';
      setError(errorMessage);
      console.error('Login error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <form className="login-form" onSubmit={handleSubmit}>
          <h2 className="login-title">Admin Portal</h2>
          <p className="login-description">Enter your credentials to access the admin panel</p>
          
          <div className="login-field">
            <label htmlFor="username" className="login-label">
              Username or Email*
            </label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              className="login-input"
              placeholder="Enter Username or Email"
              required
            />
          </div>

          <div className="login-field">
            <label htmlFor="password" className="login-label">
              Password*
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className="login-input"
              placeholder="Enter Password"
              required
            />
          </div>

          <div className="login-field">
            <label className="login-checkbox-label">
              <input
                type="checkbox"
                name="rememberMe"
                checked={formData.rememberMe}
                onChange={handleInputChange}
                className="login-checkbox"
              />
              <span>Remember me</span>
            </label>
          </div>

          {error && (
            <div className="login-error">
              {error}
            </div>
          )}

          <button 
            type="submit" 
            className="login-submit-button"
            disabled={isLoading}
          >
            {isLoading ? 'Logging in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default LoginComponent;

